/* WonderBuddy - one add-to-cart path for every custom section.
   ----------------------------------------------------------------------
   Why this file exists.

   The custom sections each had their own add-to-cart: POST /cart/add.js,
   swap the button label to "Added", done. The item really did go in - but
   nothing else on the page was told about it, so the header count stayed
   stale and the cart drawer never opened. The shopper saw "Added" and an
   unchanged cart, which reads as a failure. They only found the item by
   opening the cart themselves.

   Horizon does not poll the cart. It reacts to one event,
   'shopify:cart:lines-update', and three separate components listen for it:

     cart-icon.js          -> the count bubble, reads event.promise -> cart.totalQuantity
     cart-drawer.js        -> opens the drawer, reads event.action === 'add'
     component-cart-items  -> re-renders the drawer contents from detail.sections

   Every one of them reads event.promise, NOT the event payload. That is the
   part the old code could not have guessed: the theme wants the event the
   moment the shopper taps, carrying a promise it can await. That is how the
   drawer can start opening during the network round trip instead of after
   it. So we dispatch first and resolve second, exactly as the theme's own
   product-form.js does.

   This file talks to the theme through that documented event only. It
   imports nothing and defines no custom element, so it cannot collide with
   the theme's modules and keeps working if Horizon is updated. */
(function () {
  if (window.wbCart) { return; }

  /* The event name is a literal on purpose. The theme reaches it through
     StandardEvents.cartLinesUpdate from the '@shopify/events' module, which
     is an import-map entry only available to type="module" scripts. This
     file is a classic script so that it can be shared by sections that
     render inline handlers. The string is the contract either way. */
  var CART_LINES_UPDATE = 'shopify:cart:lines-update';

  function root() {
    var s = window.Shopify;
    return (s && s.routes && s.routes.root) || '/';
  }

  function cartIcon() {
    return document.querySelector('.header-actions__cart-icon');
  }

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* A promise plus its resolvers. The theme holds this promise while its
     drawer animates open, so it has to be handed over before the fetch
     starts and settled once the real cart is known. */
  function deferred() {
    var d = {};
    d.promise = new Promise(function (resolve, reject) {
      d.resolve = resolve;
      d.reject = reject;
    });
    /* Marks the promise handled so a failed add never shows up as an
       unhandled rejection in the console. The listeners attach their own
       .catch as well; this is only insurance for the case where the theme's
       components are not on the page at all. */
    d.promise.catch(function () {});
    return d;
  }

  /* Asking cart/add.js to also render the cart sections means the drawer
     morphs from this same response instead of firing its own follow-up
     request. If the drawer is not on the page the list is empty and we just
     do not ask for anything. */
  function cartSectionIds() {
    var ids = [];
    var nodes = document.querySelectorAll('cart-items-component[data-section-id]');
    Array.prototype.forEach.call(nodes, function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) { ids.push(id); }
    });
    return ids;
  }

  /* The count the header is showing right now. Used as the base for an
     optimistic figure so the bubble can move before the real cart comes back.
     Reads as 0 when the theme has blanked the text (a cart over 99), which the
     reconcile below then puts right. */
  function currentCount() {
    var el = document.querySelector('cart-icon .cart-bubble__text-count');
    var n = el ? parseInt((el.textContent || '').trim(), 10) : NaN;
    return isNaN(n) ? 0 : n;
  }

  /* Quietly puts the bubble right if the optimistic number was wrong - another
     tab changed the cart, or a stock cap trimmed the line. Second argument
     false so it does not replay the pop animation the shopper already saw. */
  function correctCount(real) {
    var icon = document.querySelector('cart-icon');
    if (icon && typeof icon.renderCartBubble === 'function') {
      icon.renderCartBubble(real, false);
      return;
    }
    var el = document.querySelector('cart-icon .cart-bubble__text-count');
    if (el) { el.textContent = real < 100 ? String(real) : ''; }
  }

  /* The image that flies to the cart icon. Taken from the card the button
     sits in, so a section gets the animation without wiring anything up. */
  function artworkFor(button) {
    if (!button) { return ''; }
    var card = button.closest('article, .dvp-card, .card, li, form');
    var img = card && card.querySelector('img');
    if (!img) {
      var section = button.closest('.wb-dv, .wb-plp, .wb-sbm, .wb-dvp, .wb-home');
      img = section && section.querySelector('img');
    }
    return img ? (img.currentSrc || img.src || '') : '';
  }

  /* <fly-to-cart> is one of the theme's own custom elements and is loaded on
     every page, so this borrows the theme's animation rather than inventing
     a second one that would look subtly different from the product page. */
  function flyToCart(button, image) {
    var icon = cartIcon();
    if (!icon || !button || !image) { return; }
    if (!window.customElements || !window.customElements.get('fly-to-cart')) { return; }
    if (reduceMotion()) { return; }

    var el = document.createElement('fly-to-cart');
    el.classList.add('fly-to-cart--main');
    el.style.setProperty('background-image', 'url(' + image + ')');
    el.style.setProperty('--start-opacity', '0');
    el.source = button;
    el.destination = icon;
    document.body.appendChild(el);
  }

  /**
   * Adds a variant to the cart and tells the theme about it.
   *
   * @param {object} opts
   * @param {number|string} opts.id        variant id
   * @param {number}       [opts.quantity] defaults to 1
   * @param {Element}      [opts.button]   the button pressed, for the animation
   * @param {string}       [opts.image]    overrides the auto-detected artwork
   * @returns {Promise<object>} resolves with the updated cart
   */
  function add(opts) {
    var id = Number(opts && opts.id);
    var qty = Number(opts && opts.quantity) || 1;
    var button = (opts && opts.button) || null;

    if (!id) { return Promise.reject(new Error('wbCart.add needs a variant id')); }

    var settled = deferred();

    /* Dispatched BEFORE the request, which is the whole point. The drawer
       opens on this event and fills itself in when the promise resolves, so
       the shopper gets a response in the same frame as their tap rather than
       after the round trip. Dispatching on the button matters too:
       cart-items-component ignores events raised from inside itself, and
       cart-drawer looks at the source element to decide whether it has to
       wait for a modal to close first. */
    var ev = new CustomEvent(CART_LINES_UPDATE, { bubbles: true, composed: true });
    ev.action = 'add';
    ev.context = 'product';
    ev.lines = [{ merchandiseId: String(id), quantity: qty }];
    ev.promise = settled.promise;
    (button || document.documentElement).dispatchEvent(ev);

    /* Cosmetic only. It borrows one of the theme's custom elements, so if a
       theme update changes that element this must not take the add down with
       it. */
    try { flyToCart(button, (opts && opts.image) || artworkFor(button)); } catch (e) {
      console.warn('[wb-cart] fly-to-cart skipped:', e);
    }

    var body = { items: [{ id: id, quantity: qty }] };
    var sections = cartSectionIds();
    if (sections.length) { body.sections = sections.join(','); }

    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().then(function (data) {
          /* Shopify answers a rejected add with 422 and a readable message
             ("All 3 are in your cart"), which is worth surfacing rather than
             collapsing into a generic failure. */
          if (!r.ok) {
            var err = new Error(data.description || data.message || 'Add to cart failed');
            err.cartResponse = data;
            throw err;
          }
          return data;
        });
      })
      .then(function (added) {
        /* Settle the moment the add itself is done.

           This used to wait for a second request to /cart.js so the count
           could be read back rather than guessed. Correct, but the drawer
           feeds off this same promise, so a shopper with items already in
           the cart watched an open, stale drawer for the length of an extra
           round trip. Two sequential requests is one too many for something
           that is meant to feel instant.

           So the drawer gets its rendered HTML from THIS response, and the
           bubble gets an optimistic number, and /cart.js runs afterwards
           purely to check the number and quietly fix it if it was wrong. The
           shopper sees the truth either way; they just do not wait for it. */
        var optimistic = currentCount() + qty;

        settled.resolve({
          cart: { totalQuantity: optimistic },
          detail: {
            didError: false,
            items: added.items,
            itemCount: optimistic,
            sections: added.sections,
            source: 'wb-cart',
            variantId: id
          }
        });

        notify({ button: button, image: (opts && opts.image) || artworkFor(button), title: (opts && opts.title) || titleFor(button) });

        return fetch(root() + 'cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (cart.item_count !== optimistic) { correctCount(cart.item_count); }
            return cart;
          })
          .catch(function () {
            /* The add succeeded; only the check-up failed. Not the shopper's
               problem, and not a reason to fail the caller. */
            return null;
          });
      })
      .catch(function (error) {
        /* Resolve rather than reject, with didError set. The theme reads
           this flag to mean "do not slide the drawer open", and resolving is
           what stops cart-icon and header-actions logging a warning for a
           situation they handle correctly. The caller still gets the
           rejection so it can put its own button right. */
        settled.resolve({
          cart: null,
          detail: { didError: true, itemCount: undefined, source: 'wb-cart' }
        });
        throw error;
      });
  }

  /* ---- the "added" confirmation ----------------------------------------
     Sits bottom-LEFT on purpose: the cart drawer slides in from the right, so
     anything on that side would be covered the instant the drawer opens.

     It appears the moment the add succeeds, which on a slow connection is
     still well before the drawer has finished painting - that gap is exactly
     when a shopper needs telling that something happened. role="status" means
     a screen reader announces it without stealing focus. */
  function titleFor(button) {
    if (!button) { return ''; }
    var card = button.closest('article, .dvp-card, .card, li');
    var el = card && card.querySelector('h2, h3, h4, .dvp-name, .product-link-title');
    return el ? el.textContent.trim() : '';
  }

  function openDrawer() {
    var trigger = document.querySelector('[data-testid="cart-drawer-trigger"], [aria-controls="cart-drawer"]');
    if (trigger) { trigger.click(); return true; }
    return false;
  }

  var toastEl = null;
  var toastTimer = null;

  function notify(opts) {
    var title = (opts && opts.title) || '';
    var image = (opts && opts.image) || '';

    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wb-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }

    toastEl.innerHTML =
      '<span class="wb-toast__tick" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" ' +
        'stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5 9.5 18 20 6.5"/></svg>' +
      '</span>' +
      (image ? '<img class="wb-toast__art" src="' + image + '" alt="">' : '') +
      '<span class="wb-toast__copy"><b>Added to cart</b>' +
        (title ? '<i>' + title.replace(/[<>&]/g, '') + '</i>' : '') +
      '</span>' +
      '<button type="button" class="wb-toast__cta">View cart</button>';

    toastEl.querySelector('.wb-toast__cta').addEventListener('click', function () {
      if (!openDrawer()) { window.location.href = root() + 'cart'; }
      hideToast();
    });

    /* Restart the animation for a second add rather than letting the first
       one run out - the shopper pressed again and deserves to see it again. */
    toastEl.classList.remove('is-in');
    void toastEl.offsetWidth;
    toastEl.classList.add('is-in');

    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 3400);
  }

  function hideToast() {
    if (toastEl) { toastEl.classList.remove('is-in'); }
  }

  /* ---- shared button states ---------------------------------------------
     Injected rather than added to five stylesheets, because these three
     states have to look the same on every card in the store and splitting
     them across wb-sbm.css / wb-dv.css / wb-plp.css / wb-home.css is how they
     drift apart. Deliberately low specificity and colour-free: the PDP
     buttons already style .is-added with their own green and a check icon,
     and this must lose to them rather than flatten them. */
  (function injectStates() {
    if (document.getElementById('wb-cart-states')) { return; }
    var css = [
      '.is-busy{opacity:.72;cursor:progress;pointer-events:none;}',
      '.is-error{animation:wb-cart-shake .32s ease;}',
      /* bottom-left: the cart drawer owns the right-hand side */
      '.wb-toast{position:fixed;left:16px;bottom:16px;z-index:2147483000;display:flex;align-items:center;',
      'gap:10px;max-width:min(360px,calc(100vw - 32px));padding:10px 12px;border-radius:14px;',
      'background:#1e2637;color:#fff;box-shadow:0 12px 34px rgba(16,22,35,.28);box-sizing:border-box;',
      'font-family:inherit;font-size:13px;line-height:1.3;font-weight:600;',
      'opacity:0;transform:translateY(12px);pointer-events:none;',
      'transition:opacity .22s ease,transform .22s cubic-bezier(.2,.9,.3,1.2);}',
      '.wb-toast.is-in{opacity:1;transform:none;pointer-events:auto;}',
      '.wb-toast__tick{flex:0 0 auto;display:grid;place-items:center;width:22px;height:22px;',
      'border-radius:50%;background:#2f9e5f;color:#fff;}',
      '.wb-toast__tick svg{width:13px;height:13px;stroke-dasharray:26;stroke-dashoffset:26;}',
      '.wb-toast.is-in .wb-toast__tick svg{animation:wb-tick .34s .06s ease forwards;}',
      '@keyframes wb-tick{to{stroke-dashoffset:0}}',
      '.wb-toast__art{flex:0 0 auto;width:34px;height:34px;border-radius:8px;object-fit:cover;background:#fff;}',
      '.wb-toast__copy{display:flex;flex-direction:column;min-width:0;}',
      '.wb-toast__copy b{font-weight:700;}',
      '.wb-toast__copy i{font-style:normal;font-weight:500;opacity:.75;font-size:12px;',
      'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',
      '.wb-toast__cta{flex:0 0 auto;margin-left:4px;padding:7px 11px;border:0;border-radius:9px;',
      'background:#fff;color:#1e2637;font-family:inherit;font-size:12px;font-weight:700;line-height:1;cursor:pointer;}',
      '.wb-toast__cta:hover{background:#ffe9ef;}',
      '@media (max-width:520px){.wb-toast{left:12px;right:12px;bottom:12px;max-width:none;}}',
      '@media (prefers-reduced-motion:reduce){.wb-toast{transition:opacity .18s ease;transform:none;}',
      '.wb-toast.is-in .wb-toast__tick svg{animation:none;stroke-dashoffset:0;}}',
      '@keyframes wb-cart-shake{',
      '10%,90%{transform:translateX(-1px)}',
      '30%,70%{transform:translateX(2px)}',
      '50%{transform:translateX(-2px)}',
      '100%{transform:none}}'
    ].join('');
    var el = document.createElement('style');
    el.id = 'wb-cart-states';
    el.textContent = css;
    (document.head || document.documentElement).appendChild(el);
  })();

  /* ---- button feedback ---------------------------------------------------
     Shared so the label, the timing and the disabled window are identical on
     every card in the store. Each section keeps its own class names. */
  /* Marks the button in-flight AND arms a watchdog.

     Without the watchdog a request that never comes back - a dropped
     connection, an app that swallows the response, a JS error in some other
     listener - leaves the button greyed and dead until the shopper reloads.
     A slow button is a nuisance; a permanently dead one loses the sale. So
     the state always expires on its own, whatever happens to the request. */
  function markBusy(button, ms) {
    if (!button) { return; }
    button.dataset.busy = '1';
    button.classList.add('is-busy');
    clearTimeout(button.wbBusyTimer);
    button.wbBusyTimer = setTimeout(function () {
      restore(button);
      console.warn('[wb-cart] no response after ' + ((ms || 8000) / 1000) +
        's - button released. Check the Network tab for cart/add.js.');
    }, ms || 8000);
  }

  function restore(button) {
    if (!button) { return; }
    var label = button.querySelector('[data-wb-add-label]') || button;
    var original = button.getAttribute('data-wb-original');
    if (original !== null) { label.textContent = original; }
    button.classList.remove('is-busy', 'is-added', 'is-error');
    button.removeAttribute('data-wb-original');
    button.dataset.busy = '0';
    clearTimeout(button.wbBusyTimer);
  }

  function flash(button, addedLabel, ms) {
    if (!button) { return; }
    var label = button.querySelector('[data-wb-add-label]') || button;
    var original = button.getAttribute('data-wb-original');
    if (original === null) {
      original = label.textContent;
      button.setAttribute('data-wb-original', original);
    }
    label.textContent = addedLabel || 'Added';
    button.classList.add('is-added');
    clearTimeout(button.wbBusyTimer);
    clearTimeout(button.wbResetTimer);
    button.wbResetTimer = setTimeout(function () {
      label.textContent = original;
      button.classList.remove('is-added');
      button.classList.remove('is-busy');
      button.removeAttribute('data-wb-original');
      button.dataset.busy = '0';
    }, ms || 1800);
  }

  function fail(button, message) {
    if (!button) { return; }
    var label = button.querySelector('[data-wb-add-label]') || button;
    var original = button.getAttribute('data-wb-original');
    if (original === null) { original = label.textContent; }
    label.textContent = message || 'Try again';
    button.classList.add('is-error');
    clearTimeout(button.wbBusyTimer);
    clearTimeout(button.wbResetTimer);
    button.wbResetTimer = setTimeout(function () {
      label.textContent = original;
      button.classList.remove('is-error', 'is-busy');
      button.removeAttribute('data-wb-original');
      button.dataset.busy = '0';
    }, 2600);
  }

  /* ---- plain cart forms --------------------------------------------------
     Two sections still post a real <form> to /cart/add, which reloads the
     whole page onto the cart. Intercepting the submit upgrades them without
     touching their markup - and because the form is still a real form, a
     visitor with JavaScript blocked keeps the working, if slower, version.

     Scoped to the WonderBuddy sections so the theme's own product forms,
     which have their own handler inside <product-form-component>, are never
     touched. */
  var WB_ROOTS = '.wb-dv, .wb-plp, .wb-sbm, .wb-dvp, .wb-home, .wb-cl';

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (e.defaultPrevented || !form || form.tagName !== 'FORM') { return; }

    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) { return; }
    if (!form.closest(WB_ROOTS)) { return; }
    if (form.closest('product-form-component')) { return; }

    var idField = form.querySelector('[name="id"]');
    if (!idField || !idField.value) { return; }

    e.preventDefault();

    var button = form.querySelector('button[type="submit"], button:not([type])');
    if (button && button.dataset.busy === '1') { return; }
    markBusy(button);

    var qtyField = form.querySelector('[name="quantity"]');
    add({
      id: idField.value,
      quantity: qtyField ? parseInt(qtyField.value, 10) || 1 : 1,
      button: button
    })
      .then(function () { flash(button, form.getAttribute('data-added-label') || 'Added'); })
      .catch(function (error) { fail(button, error && error.message); });
  });

  window.wbCart = { add: add, flash: flash, fail: fail, busy: markBusy, restore: restore, notify: notify, event: CART_LINES_UPDATE };
})();
